import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "quoteArea", "loadlistArea", "quoteDocument", "loadlistDocument",
    "quoteTab", "loadlistTab",
    "typePill", "companyNameInput", "logoFileInput", "logoPreview",
    "styleBtn",
    "saveBar", "actionBar",
    "modalThumb", "modalThumbTitle", "modalThumbSub", "modalThumbBadge"
  ]

  static values = {
    demoData: { type: Object, default: {} },
    signedIn: { type: Boolean, default: false }
  }

  connect() {
    this.currentType = 'marquee_hire'
    this.currentView = 'quote'
    this.brandColor = '#1a6b4a'
    this.companyName = 'Your Company'
    this.logoDataUrl = null
    this.quoteStyle = 'bold'

    this.restoreFromSession()
    this.applyBrandColor()
    this.renderQuote()
    this.renderLoadList()
    this.updateSignedInState()
  }

  // -- Company type switching -----------------------------------------------

  switchType(event) {
    const type = event.params.type
    if (type === this.currentType) return

    this.currentType = type
    this.typePillTargets.forEach(pill => {
      pill.classList.toggle('business-builder__type-pill--active', pill.dataset.quoteBuilderTypeParam === type)
    })

    this.renderQuote()
    this.renderLoadList()
    this.saveToSession()
  }

  // -- View toggle (Quote / Load List) --------------------------------------

  switchView(event) {
    const view = event.params.view
    if (view === this.currentView) return

    this.currentView = view

    if (this.hasQuoteTabTarget && this.hasLoadlistTabTarget) {
      this.quoteTabTarget.classList.toggle('business-builder__tab--active', view === 'quote')
      this.loadlistTabTarget.classList.toggle('business-builder__tab--active', view === 'loadlist')
    }

    if (this.hasQuoteAreaTarget) this.quoteAreaTarget.style.display = view === 'quote' ? '' : 'none'
    if (this.hasLoadlistAreaTarget) this.loadlistAreaTarget.style.display = view === 'loadlist' ? '' : 'none'
  }

  // -- Brand customisation --------------------------------------------------

  updateCompanyName() {
    this.companyName = this.companyNameInputTarget.value || 'Your Company'
    const el = this.quoteDocumentTarget.querySelector('.bb-quote__company-name')
    if (el) el.textContent = this.companyName

    const llEl = this.loadlistDocumentTarget.querySelector('.bb-ll__company-name')
    if (llEl) llEl.textContent = this.companyName

    this.saveToSession()
  }

  selectColor(event) {
    this.brandColor = event.params.color
    this.applyBrandColor()

    this.element.querySelectorAll('.business-builder__swatch').forEach(s =>
      s.classList.remove('business-builder__swatch--active')
    )
    event.currentTarget.classList.add('business-builder__swatch--active')

    this.renderQuote()
    this.renderLoadList()
    this.saveToSession()
  }

  customColor() {
    const color = prompt('Enter a hex color (e.g. #ff6600):')
    if (color && /^#[0-9a-fA-F]{6}$/.test(color)) {
      this.brandColor = color
      this.element.querySelectorAll('.business-builder__swatch').forEach(s =>
        s.classList.remove('business-builder__swatch--active')
      )
      this.applyBrandColor()
      this.renderQuote()
      this.renderLoadList()
      this.saveToSession()
    }
  }

  applyBrandColor() {
    this.element.style.setProperty('--bb-brand', this.brandColor)
  }

  selectStyle(event) {
    this.quoteStyle = event.params.style
    this.styleBtnTargets.forEach(btn => {
      btn.classList.toggle('business-builder__style-btn--active', btn.dataset.quoteBuilderStyleParam === this.quoteStyle)
    })

    const doc = this.quoteDocumentTarget
    doc.className = `bb-quote bb-quote--${this.quoteStyle}`
    this.saveToSession()
  }

  triggerLogoUpload() {
    this.logoFileInputTarget.click()
  }

  handleLogoUpload(event) {
    const file = event.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      this.logoDataUrl = e.target.result
      this.logoPreviewTarget.innerHTML = `<img src="${this.logoDataUrl}" alt="Logo" class="business-builder__logo-img">`
      this.updateLogoInPreview()
      this.saveToSession()
    }
    reader.readAsDataURL(file)
  }

  updateLogoInPreview() {
    const el = this.quoteDocumentTarget.querySelector('.bb-quote__logo')
    if (el) {
      el.innerHTML = this.logoDataUrl
        ? `<img src="${this.logoDataUrl}" alt="Logo" class="bb-quote__logo-img">`
        : ''
    }
  }

  // -- Quote rendering ------------------------------------------------------

  renderQuote() {
    const data = this.currentData()
    if (!data) return

    const q = data.quote
    const c = data.customer

    const itemsByCategory = this.groupBy(q.items, 'category')

    const itemsHtml = Object.entries(itemsByCategory).map(([cat, items]) => `
      <div class="bb-quote__category">
        <div class="bb-quote__category-header">${this.esc(cat)}</div>
        ${items.map(item => `
          <div class="bb-quote__item-row">
            <span class="bb-quote__item-name">${this.esc(item.name)}</span>
            <span class="bb-quote__item-qty">${item.qty}</span>
            <span class="bb-quote__item-price">&pound;${item.unit_price.toFixed(2)}</span>
            <span class="bb-quote__item-total">&pound;${item.total.toFixed(2)}</span>
          </div>
        `).join('')}
      </div>
    `).join('')

    const extrasHtml = q.optional_extras.map(ex => `
      <div class="bb-quote__extra-row">
        <span class="bb-quote__extra-name">${this.esc(ex.name)}</span>
        <span class="bb-quote__extra-price">+ &pound;${ex.price.toFixed(2)}</span>
      </div>
    `).join('')

    this.quoteDocumentTarget.className = `bb-quote bb-quote--${this.quoteStyle}`
    this.quoteDocumentTarget.innerHTML = `
      <div class="bb-quote__topbar" style="background:${this.brandColor}">
        <div class="bb-quote__topbar-left">
          <div class="bb-quote__logo"></div>
          <span class="bb-quote__company-name">${this.esc(this.companyName)}</span>
        </div>
        <div class="bb-quote__topbar-right">
          <span class="bb-quote__number">${this.esc(q.number)}</span>
        </div>
      </div>

      <div class="bb-quote__hero">
        <div class="bb-quote__hero-left">
          <div class="bb-quote__hero-label">Prepared for</div>
          <div class="bb-quote__hero-name">${this.esc(c.name)}</div>
          <div class="bb-quote__hero-detail">${this.esc(c.event_name)} &middot; ${this.esc(c.event_date)}</div>
          <div class="bb-quote__hero-detail">${this.esc(c.venue)}</div>
          <div class="bb-quote__hero-detail">${c.guests} guests</div>
        </div>
        <div class="bb-quote__hero-right">
          <div class="bb-quote__hero-label">Quote date</div>
          <div class="bb-quote__hero-value">${this.esc(q.date)}</div>
          <div class="bb-quote__hero-label bb-quote__hero-label--mt">Valid until</div>
          <div class="bb-quote__hero-value">${this.esc(q.valid_until)}</div>
        </div>
      </div>

      <div class="bb-quote__items-header">
        <span class="bb-quote__items-col bb-quote__items-col--name">Item</span>
        <span class="bb-quote__items-col bb-quote__items-col--qty">Qty</span>
        <span class="bb-quote__items-col bb-quote__items-col--price">Unit Price</span>
        <span class="bb-quote__items-col bb-quote__items-col--total">Total</span>
      </div>

      ${itemsHtml}

      <div class="bb-quote__totals">
        <div class="bb-quote__totals-row">
          <span>Subtotal</span>
          <span>&pound;${q.subtotal.toFixed(2)}</span>
        </div>
        <div class="bb-quote__totals-row">
          <span>VAT (20%)</span>
          <span>&pound;${q.vat.toFixed(2)}</span>
        </div>
        <div class="bb-quote__totals-row bb-quote__totals-row--grand">
          <span>Total</span>
          <span>&pound;${q.total.toFixed(2)}</span>
        </div>
      </div>

      <div class="bb-quote__extras">
        <div class="bb-quote__extras-title">Optional Extras</div>
        ${extrasHtml}
      </div>

      <div class="bb-quote__notes">
        <div class="bb-quote__notes-title">Notes</div>
        <p class="bb-quote__notes-text">${this.esc(q.notes)}</p>
      </div>

      <div class="bb-quote__signature">
        <div class="bb-quote__sig-block">
          <div class="bb-quote__sig-line"></div>
          <div class="bb-quote__sig-label">Client signature</div>
        </div>
        <div class="bb-quote__sig-block">
          <div class="bb-quote__sig-line"></div>
          <div class="bb-quote__sig-label">Date</div>
        </div>
      </div>

      <div class="bb-quote__footer">
        <span>Generated with</span>
        <strong>GoodEvent Business</strong>
      </div>
    `

    this.updateLogoInPreview()
  }

  // -- Load list rendering --------------------------------------------------

  renderLoadList() {
    const data = this.currentData()
    if (!data) return

    const ll = data.load_list
    const totalItems = ll.categories.reduce((sum, cat) => sum + cat.items.length, 0)
    const totalWeight = ll.categories.reduce((sum, cat) =>
      sum + cat.items.reduce((s, i) => s + i.weight_kg, 0), 0
    ).toFixed(1)

    const categoriesHtml = ll.categories.map(cat => {
      const catWeight = cat.items.reduce((s, i) => s + i.weight_kg, 0).toFixed(1)
      return `
        <div class="bb-ll__category">
          <div class="bb-ll__category-header">
            <span class="bb-ll__category-name">${this.esc(cat.name)}</span>
            <span class="bb-ll__category-weight">${catWeight} kg</span>
          </div>
          ${cat.items.map(item => `
            <div class="bb-ll__item-row">
              <span class="bb-ll__item-name">${this.esc(item.name)}</span>
              <span class="bb-ll__item-qty">x${item.qty}</span>
              <span class="bb-ll__item-weight">${item.weight_kg} kg</span>
              <span class="bb-ll__item-check bb-ll__item-check--van" data-action="click->quote-builder#toggleCheck">
                <i class="far fa-square"></i>
              </span>
              <span class="bb-ll__item-check bb-ll__item-check--site" data-action="click->quote-builder#toggleCheck">
                <i class="far fa-square"></i>
              </span>
            </div>
          `).join('')}
        </div>
      `
    }).join('')

    this.loadlistDocumentTarget.innerHTML = `
      <div class="bb-ll__topbar" style="background:${this.brandColor}">
        <span class="bb-ll__company-name">${this.esc(this.companyName)}</span>
        <span class="bb-ll__job-ref">${this.esc(ll.job_ref)}</span>
      </div>

      <div class="bb-ll__job-details">
        <div class="bb-ll__detail"><strong>Event:</strong> ${this.esc(ll.event_name)}</div>
        <div class="bb-ll__detail"><strong>Date:</strong> ${this.esc(ll.event_date)}</div>
        <div class="bb-ll__detail"><strong>Venue:</strong> ${this.esc(ll.venue)}</div>
        <div class="bb-ll__detail"><strong>Crew Lead:</strong> ${this.esc(ll.crew_lead)}</div>
        <div class="bb-ll__detail"><strong>Vehicle:</strong> ${this.esc(ll.vehicle)}</div>
      </div>

      <div class="bb-ll__progress">
        <div class="bb-ll__progress-bar">
          <div class="bb-ll__progress-fill" style="width:0%"></div>
        </div>
        <span class="bb-ll__progress-text">0 / ${totalItems} items picked</span>
      </div>

      <div class="bb-ll__col-headers">
        <span class="bb-ll__col bb-ll__col--name">Item</span>
        <span class="bb-ll__col bb-ll__col--qty">Qty</span>
        <span class="bb-ll__col bb-ll__col--weight">Weight</span>
        <span class="bb-ll__col bb-ll__col--van">Van</span>
        <span class="bb-ll__col bb-ll__col--site">Site</span>
      </div>

      ${categoriesHtml}

      <div class="bb-ll__summary">
        <span>${totalItems} items</span>
        <span>${totalWeight} kg total</span>
      </div>
    `
  }

  toggleCheck(event) {
    const el = event.currentTarget
    const icon = el.querySelector('i')
    const checked = icon.classList.contains('fa-check-square')

    if (checked) {
      icon.classList.remove('fas', 'fa-check-square')
      icon.classList.add('far', 'fa-square')
      el.classList.remove('bb-ll__item-check--done')
    } else {
      icon.classList.remove('far', 'fa-square')
      icon.classList.add('fas', 'fa-check-square')
      el.classList.add('bb-ll__item-check--done')
    }

    this.updateProgress()
  }

  updateProgress() {
    const allChecks = this.loadlistDocumentTarget.querySelectorAll('.bb-ll__item-check--van')
    const doneChecks = this.loadlistDocumentTarget.querySelectorAll('.bb-ll__item-check--van.bb-ll__item-check--done')
    const total = allChecks.length
    const done = doneChecks.length
    const pct = total > 0 ? Math.round((done / total) * 100) : 0

    const fill = this.loadlistDocumentTarget.querySelector('.bb-ll__progress-fill')
    const text = this.loadlistDocumentTarget.querySelector('.bb-ll__progress-text')
    if (fill) fill.style.width = `${pct}%`
    if (text) text.textContent = `${done} / ${total} items picked`
  }

  // -- Signup / save / action bars ------------------------------------------

  openSignupModal() {
    if (this.hasModalThumbTitleTarget) {
      this.modalThumbTitleTarget.textContent = `${this.companyName} — Quote`
    }
    if (this.hasModalThumbSubTarget) {
      const data = this.currentData()
      this.modalThumbSubTarget.textContent = data ? `${data.quote.items.length} items &middot; ${data.company_label}` : 'Customised preview'
    }

    const signupModal = this.element.querySelector('[data-controller="signup-modal"]')
    if (signupModal) {
      const controller = this.application.getControllerForElementAndIdentifier(signupModal, 'signup-modal')
      if (controller) controller.open()
    }
  }

  downloadPdf() {
    // Placeholder — future PDF generation
  }

  shareQuote() {
    // Placeholder — future share flow
  }

  newJob() {
    // Placeholder — reset to fresh quote
  }

  updateSignedInState() {
    if (this.signedInValue) {
      if (this.hasSaveBarTarget) this.saveBarTarget.style.display = 'none'
      if (this.hasActionBarTarget) this.actionBarTarget.style.display = ''
    } else {
      if (this.hasSaveBarTarget) this.saveBarTarget.style.display = ''
      if (this.hasActionBarTarget) this.actionBarTarget.style.display = 'none'
    }
  }

  // -- Session storage persistence ------------------------------------------

  saveToSession() {
    const state = {
      currentType: this.currentType,
      brandColor: this.brandColor,
      companyName: this.companyName,
      logoDataUrl: this.logoDataUrl,
      quoteStyle: this.quoteStyle
    }
    try {
      sessionStorage.setItem('bb_state', JSON.stringify(state))
    } catch { /* ignore quota errors */ }
  }

  restoreFromSession() {
    try {
      const raw = sessionStorage.getItem('bb_state')
      if (!raw) return

      const state = JSON.parse(raw)
      if (state.currentType) this.currentType = state.currentType
      if (state.brandColor) this.brandColor = state.brandColor
      if (state.companyName) this.companyName = state.companyName
      if (state.logoDataUrl) this.logoDataUrl = state.logoDataUrl
      if (state.quoteStyle) this.quoteStyle = state.quoteStyle

      // Restore UI elements
      if (this.hasCompanyNameInputTarget) this.companyNameInputTarget.value = this.companyName

      this.typePillTargets.forEach(pill => {
        pill.classList.toggle('business-builder__type-pill--active', pill.dataset.quoteBuilderTypeParam === this.currentType)
      })

      this.styleBtnTargets.forEach(btn => {
        btn.classList.toggle('business-builder__style-btn--active', btn.dataset.quoteBuilderStyleParam === this.quoteStyle)
      })

      if (this.logoDataUrl && this.hasLogoPreviewTarget) {
        this.logoPreviewTarget.innerHTML = `<img src="${this.logoDataUrl}" alt="Logo" class="business-builder__logo-img">`
      }
    } catch { /* ignore parse errors */ }
  }

  // -- Helpers --------------------------------------------------------------

  currentData() {
    return this.demoDataValue[this.currentType] || null
  }

  groupBy(arr, key) {
    return arr.reduce((groups, item) => {
      const val = item[key] || 'Other'
      ;(groups[val] = groups[val] || []).push(item)
      return groups
    }, {})
  }

  esc(str) {
    if (!str) return ''
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }
}
